/**
 * <s-pizza-builder> — step-by-step pizza builder with an interactive viewer.
 * Left / Whole / Right buttons under each topping set that topping's hidden input, which is sent as a
 * line item property ("Pepperoni: Left half"), so staff see exact choices on the order. Crust and bake
 * are plain radios with their own properties. Price is the product's price; toppings never change it.
 *
 * Viewer: the layered photo pizza can be spun (drag sideways or the Spin buttons), tilted back like it's
 * on the table (the toppings sit on a raised layer, so they shift over the cheese), and zoomed. This is a
 * CSS 3D transform of photo layers, not a 3D model.
 */
const ZOOM_STEPS = [1, 1.35, 1.75, 2.2];
const TILT_DEG = 52;

class SPizzaBuilder extends HTMLElement {
  #ac;
  #armed = null;
  #view = { spin: 0, tilt: 0, zoom: 0 };
  #drag = null;
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.addEventListener('change', () => this.#sync(), { signal });
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('product-form:added', (event) => this.#onAdded(event), { signal });

    const stage = this.querySelector('[data-pizza-stage]');
    if (stage) {
      stage.addEventListener('pointerdown', (event) => this.#dragStart(event, stage), { signal });
      stage.addEventListener('pointermove', (event) => this.#dragMove(event, stage), { signal });
      stage.addEventListener('pointerup', (event) => this.#dragEnd(event, stage), { signal });
      stage.addEventListener('pointercancel', (event) => this.#dragEnd(event, stage), { signal });
      stage.addEventListener('pointerleave', () => this.#untilt(), { signal });

      // Phones: show the mini live pizza bar once the big viewer scrolls out of sight.
      if ('IntersectionObserver' in window) {
        this.#observer = new IntersectionObserver(([entry]) => {
          this.dataset.mini = String(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
        this.#observer.observe(stage);
      }
    }
    this.#applyView();
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#bakeTimer);
    clearTimeout(this.#sprinkleTimer);
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // ----- Viewer -----

  #photo() {
    return this.querySelector('[data-pizza-stage] .pizza-photo');
  }

  #applyView() {
    const photo = this.#photo();
    const stage = this.querySelector('[data-pizza-stage]');
    if (!photo || !stage) return;
    const zoom = ZOOM_STEPS[this.#view.zoom];
    photo.style.setProperty('--rz', `${this.#view.spin.toFixed(1)}deg`);
    photo.style.setProperty('--tilt', `${this.#view.tilt.toFixed(1)}deg`);
    photo.style.setProperty('--zoom', String(zoom));
    stage.style.setProperty('--zoom', String(zoom));
    stage.style.setProperty('--tiltn', String((this.#view.tilt / TILT_DEG).toFixed(3)));
    stage.dataset.tilted = String(this.#view.tilt > 0);

    const tiltButton = this.querySelector('[data-view="tilt"]');
    tiltButton?.setAttribute('aria-pressed', String(this.#view.tilt > 0));
    const zoomIn = this.querySelector('[data-view="zoom-in"]');
    const zoomOut = this.querySelector('[data-view="zoom-out"]');
    if (zoomIn) zoomIn.disabled = this.#view.zoom >= ZOOM_STEPS.length - 1;
    if (zoomOut) zoomOut.disabled = this.#view.zoom <= 0;
  }

  #onView(action) {
    this.dataset.interacted = 'true';
    switch (action) {
      case 'spin-left':
        this.#view.spin -= 45;
        break;
      case 'spin-right':
        this.#view.spin += 45;
        break;
      case 'tilt':
        this.#view.tilt = this.#view.tilt > 0 ? 0 : TILT_DEG;
        break;
      case 'zoom-in':
        this.#view.zoom = Math.min(this.#view.zoom + 1, ZOOM_STEPS.length - 1);
        break;
      case 'zoom-out':
        this.#view.zoom = Math.max(this.#view.zoom - 1, 0);
        break;
      default:
        this.#view = { spin: 0, tilt: 0, zoom: 0 };
    }
    this.#applyView();
  }

  #dragStart(event, stage) {
    if (event.button !== undefined && event.button !== 0) return;
    this.#drag = { x: event.clientX, y: event.clientY, spin: this.#view.spin, tilt: this.#view.tilt, id: event.pointerId, moved: false };
  }

  #dragMove(event, stage) {
    if (!this.#drag) {
      this.#hoverTilt(event);
      return;
    }
    const dx = event.clientX - this.#drag.x;
    const dy = event.clientY - this.#drag.y;
    if (!this.#drag.moved) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      // On touch, vertical swipes scroll the page; only sideways drags spin the pizza.
      if (event.pointerType !== 'mouse' && Math.abs(dy) > Math.abs(dx)) {
        this.#drag = null;
        return;
      }
      this.#drag.moved = true;
      stage.setPointerCapture?.(this.#drag.id);
      stage.dataset.dragging = 'true';
      this.dataset.interacted = 'true';
      this.#untilt();
    }
    this.#view.spin = this.#drag.spin + dx * 0.6;
    if (event.pointerType === 'mouse') {
      this.#view.tilt = Math.min(Math.max(this.#drag.tilt - dy * 0.35, 0), TILT_DEG + 8);
    }
    this.#applyView();
  }

  #dragEnd(event, stage) {
    if (this.#drag?.moved) stage.releasePointerCapture?.(this.#drag.id);
    this.#drag = null;
    delete stage.dataset.dragging;
  }

  // Gentle lean toward a mouse pointer when not dragging, so the pizza feels like it's in front of you.
  #hoverTilt(event) {
    if (event.pointerType !== 'mouse' || this.#reducedMotion) return;
    const photo = this.#photo();
    if (!photo) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    photo.style.setProperty('--ry', `${(x * 7).toFixed(2)}deg`);
    photo.style.setProperty('--rx', `${(-y * 7).toFixed(2)}deg`);
  }

  #untilt() {
    const photo = this.#photo();
    photo?.style.removeProperty('--rx');
    photo?.style.removeProperty('--ry');
  }

  // ----- Oven moment after a successful add (the product form holds the cart drawer ~1.9s) -----

  // Add-on cards (wings, WingBites, drinks) are separate forms: note them as sides, no oven moment.
  #sides = [];
  #onAdded(event) {
    const addon = event.target.closest?.('[data-pizza-addon]');
    if (!addon) {
      this.#bake();
      return;
    }
    const name = addon.dataset.addonName;
    if (name) this.#sides.push(name);
    const done = addon.querySelector('[data-addon-added]');
    if (done) done.hidden = false;
    const status = this.querySelector('[data-pizza-status]');
    if (status && name) status.textContent = (this.dataset.msgSide || '__T__').replace('__T__', name);
    this.#sync();
  }

  #bakeTimer;
  #bake() {
    clearTimeout(this.#bakeTimer);
    delete this.dataset.baked;
    void this.offsetWidth; // restart the CSS animations on repeat adds
    this.dataset.baked = 'true';
    const status = this.querySelector('[data-pizza-status]');
    if (status && this.dataset.msgOven) status.textContent = this.dataset.msgOven;
    this.#bakeTimer = setTimeout(() => delete this.dataset.baked, 2600);
  }

  // ----- Toppings -----

  #input(topping) {
    return this.querySelector(`input[data-topping-input="${topping}"]`);
  }

  #placementOf(topping) {
    const value = this.#input(topping)?.value || '';
    if (value === this.dataset.labelLeft) return 'left';
    if (value === this.dataset.labelRight) return 'right';
    if (value === this.dataset.labelWhole) return 'whole';
    return 'none';
  }

  #setPlacement(topping, placement) {
    const input = this.#input(topping);
    if (!input) return;
    const labels = { left: this.dataset.labelLeft, right: this.dataset.labelRight, whole: this.dataset.labelWhole, none: '' };
    input.value = labels[placement] ?? '';
  }

  #nameOf(topping) {
    return this.querySelector(`[data-topping-tile="${topping}"]`)?.dataset.name || topping;
  }

  #announce(placement, topping) {
    const status = this.querySelector('[data-pizza-status]');
    if (!status) return;
    const name = this.#nameOf(topping);
    if (placement === 'none') {
      status.textContent = (this.dataset.msgRemoved || '__T__').replace('__T__', name);
      return;
    }
    const labels = { left: this.dataset.labelLeft, right: this.dataset.labelRight, whole: this.dataset.labelWhole };
    status.textContent = (this.dataset.msgPlaced || '__T__: __P__').replace('__T__', name).replace('__P__', labels[placement]);
  }

  #onClick(event) {
    const view = event.target.closest('[data-view]');
    if (view && this.contains(view)) {
      this.#onView(view.dataset.view);
      return;
    }

    // Big topping button: adds it to the whole pizza, or takes it off.
    const toggle = event.target.closest('[data-toggle-topping]');
    if (toggle && this.contains(toggle)) {
      const topping = toggle.closest('[data-topping-tile]')?.dataset.toppingTile;
      if (!topping) return;
      const placement = this.#placementOf(topping) === 'none' ? 'whole' : 'none';
      this.#setPlacement(topping, placement);
      this.#announce(placement, topping);
      this.#sync();
      this.#sprinkle(placement);
      return;
    }

    // Quick start: replaces the toppings with a preset (all on the whole pizza). Everything stays editable.
    const preset = event.target.closest('[data-preset]');
    if (preset && this.contains(preset)) {
      const wanted = new Set(preset.dataset.preset.split(','));
      this.querySelectorAll('[data-topping-tile]').forEach((tile) => {
        const topping = tile.dataset.toppingTile;
        this.#setPlacement(topping, wanted.has(topping) ? 'whole' : 'none');
      });
      const status = this.querySelector('[data-pizza-status]');
      if (status) status.textContent = (this.dataset.msgPreset || '__T__').replace('__T__', preset.dataset.presetName || '');
      this.#sync();
      this.#sprinkle('whole');
      return;
    }

    if (event.target.closest('[data-clear-toppings]')) {
      this.querySelectorAll('[data-topping-tile]').forEach((tile) => this.#setPlacement(tile.dataset.toppingTile, 'none'));
      const status = this.querySelector('[data-pizza-status]');
      if (status) status.textContent = this.dataset.msgCleared || '';
      this.#sync();
      return;
    }

    // Whole / Left / Right buttons (shown once a topping is added) move it around the pizza.
    const setPlace = event.target.closest('[data-set-place]');
    if (setPlace && this.contains(setPlace)) {
      const topping = setPlace.closest('[data-topping-tile]')?.dataset.toppingTile;
      if (!topping) return;
      const wanted = setPlace.dataset.setPlace;
      const placement = wanted;
      this.#setPlacement(topping, placement);
      this.#armed = placement === 'none' ? null : topping;
      this.#announce(placement, topping);
      this.#sync();
      this.#sprinkle(placement);
    }
  }

  // Each topping: a hand shadow sweeps the target half, the pizza settles, phones buzz lightly.
  #sprinkleTimer;
  #sprinkle(placement) {
    const stage = this.querySelector('[data-pizza-stage]');
    if (!stage || placement === 'none') return;
    clearTimeout(this.#sprinkleTimer);
    delete stage.dataset.sprinkle;
    void stage.offsetWidth;
    stage.dataset.sprinkle = placement;
    this.#sprinkleTimer = setTimeout(() => delete stage.dataset.sprinkle, 1100);
    if (!this.#reducedMotion) navigator.vibrate?.(12);
  }

  #text(selector, value) {
    this.querySelectorAll(selector).forEach((el) => {
      el.textContent = value;
    });
  }

  #sync() {
    const crust = this.querySelector('input[data-crust]:checked');
    const bake = this.querySelector('input[data-bake]:checked');
    if (crust) this.dataset.crust = crust.dataset.crust;
    if (bake) this.dataset.bake = bake.dataset.bake;

    const halves = { whole: [], left: [], right: [] };
    let split = false;

    this.querySelectorAll('[data-topping-tile]').forEach((tile) => {
      const topping = tile.dataset.toppingTile;
      const placement = this.#placementOf(topping);
      const onLeft = placement === 'left' || placement === 'whole';
      const onRight = placement === 'right' || placement === 'whole';
      // Updates the big viewer and the phone mini pizza together.
      this.querySelectorAll(`[data-topping="${topping}"][data-half]`).forEach((group) => {
        group.dataset.on = String(group.dataset.half === 'left' ? onLeft : onRight);
      });

      tile.dataset.placed = placement;
      tile.querySelectorAll('[data-set-place]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.setPlace === placement));
      });

      if (placement === 'none') return;
      if (placement !== 'whole') split = true;
      halves[placement].push(tile.dataset.name || topping);
    });

    this.dataset.split = String(split);
    this.dataset.armed = String(Boolean(this.#armed));

    // Review card
    const count = halves.whole.length + halves.left.length + halves.right.length;
    if (crust) this.#text('[data-summary="crust"]', crust.value);
    if (bake) this.#text('[data-summary="bake"]', bake.value);
    for (const side of ['whole', 'left', 'right']) {
      const row = this.querySelector(`[data-summary-row="${side}"]`);
      if (row) row.hidden = halves[side].length === 0;
      this.#text(`[data-summary="${side}"]`, halves[side].join(', '));
    }
    const cheeseRow = this.querySelector('[data-summary-row="cheese"]');
    if (cheeseRow) cheeseRow.hidden = count > 0;

    // Step bar
    const countLabel = count === 0
      ? this.dataset.labelCheese
      : count === 1
        ? this.dataset.msgOne
        : (this.dataset.msgMany || '__N__').replace('__N__', String(count));
    const crustShort = this.dataset.crust === 'thin' ? this.dataset.shortThin : this.dataset.shortOriginal;
    const bakeShort = this.dataset.bake === 'extra' ? this.dataset.shortExtra : this.dataset.shortRegular;
    this.#text('[data-step-status="crust"]', crustShort || '');
    this.#text('[data-step-status="toppings"]', countLabel || '');
    this.#text('[data-step-status="bake"]', bakeShort || '');

    // Sides added from the add-on cards (each is its own cart line)
    if (this.#sides.length) {
      const counts = this.#sides.reduce((acc, name) => acc.set(name, (acc.get(name) || 0) + 1), new Map());
      this.#text('[data-summary="sides"]', [...counts].map(([name, n]) => (n > 1 ? `${name} ×${n}` : name)).join(', '));
    }

    // Quick start buttons light up when the pizza matches them exactly.
    const active = [];
    this.querySelectorAll('[data-topping-tile]').forEach((tile) => {
      if (tile.dataset.placed === 'whole') active.push(tile.dataset.toppingTile);
      else if (tile.dataset.placed !== 'none') active.push(`${tile.dataset.toppingTile}:half`);
    });
    const activeKey = active.sort().join(',');
    this.querySelectorAll('[data-preset]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.preset.split(',').sort().join(',') === activeKey));
    });
    this.querySelectorAll('[data-toggle-topping]').forEach((button) => {
      const tile = button.closest('[data-topping-tile]');
      button.setAttribute('aria-pressed', String(tile?.dataset.placed !== 'none'));
    });

    // Phone mini bar
    const parts = [crustShort, count ? countLabel : this.dataset.labelCheese, bakeShort].filter(Boolean);
    this.#text('[data-pizza-mini-text]', parts.join(' · '));
  }
}

if (!customElements.get('s-pizza-builder')) customElements.define('s-pizza-builder', SPizzaBuilder);
